import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import AppUpdateWatcher from '@/shared/components/AppUpdateWatcher';
import { ProtectedRoute, PublicOnlyRoute, MustChangePasswordGuard } from '@/app/ProtectedRoute';
import { ROLE_ZAPISU_FORMULARZY } from '@/shared/lib/utils';
import { Loader2 } from 'lucide-react';

// ─── Lazy page imports ────────────────────────────────────────────────────────

const LoginPage        = lazy(() => import('@features/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const ResetPasswordPage = lazy(() => import('@features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const ActivatePage      = lazy(() => import('@features/auth/ActivatePage').then((m) => ({ default: m.ActivatePage })));
const DashboardPage    = lazy(() => import('@features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const AnalyticsPage    = lazy(() => import('@features/analytics/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const AdminUsersPage           = lazy(() => import('@features/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
const AdminDriversPage         = lazy(() => import('@features/admin/AdminDriversPage').then((m) => ({ default: m.AdminDriversPage })));
const CepikVerificationPage    = lazy(() => import('@features/admin/cepik/CepikVerificationView').then((m) => ({ default: m.CepikVerificationPage })));
const AdminVehiclesPage        = lazy(() => import('@features/admin/AdminVehiclesPage').then((m) => ({ default: m.AdminVehiclesPage })));
const AdminTaskAssignmentsPage = lazy(() => import('@features/admin/AdminTaskAssignmentsPage').then((m) => ({ default: m.AdminTaskAssignmentsPage })));
const FormBuilderListPage = lazy(() => import('@features/admin/forms/FormBuilderPage').then((m) => ({ default: m.FormBuilderPage })));
const FormDesignerPage    = lazy(() => import('@features/admin/forms/FormDesignerPage').then((m) => ({ default: m.FormDesignerPage })));
const InstructorsPage     = lazy(() => import('@features/instructors/InstructorsPage').then((m) => ({ default: m.InstructorsPage })));
const ReportsPage         = lazy(() => import('@features/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const ReportCreatorPage      = lazy(() => import('@features/reports/ReportCreatorPage').then((m) => ({ default: m.ReportCreatorPage })));
const NotificationsPage      = lazy(() => import('@features/notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const MessagesPage           = lazy(() => import('@features/messages/MessagesPage').then((m) => ({ default: m.MessagesPage })));
const ChangePasswordPage     = lazy(() => import('@features/auth/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage })));
const FormPage         = lazy(() => import('@features/forms/FormPage').then((m) => ({ default: m.FormPage })));
const FormsListPage    = lazy(() => import('@features/forms/FormsListPage').then((m) => ({ default: m.FormsListPage })));
// B-655: rejestr „Kontrole trzeźwości” (Formularze › Kontrole trzeźwości).
const KontroleTrzezwosciPage = lazy(() => import('@features/forms/KontroleTrzezwosciPage').then((m) => ({ default: m.KontroleTrzezwosciPage })));
const KontrolePredkosciPage = lazy(() => import('@features/forms/KontrolePredkosciPage').then((m) => ({ default: m.KontrolePredkosciPage })));
const IncidentsPage    = lazy(() => import('@features/incidents/IncidentsPage').then((m) => ({ default: m.IncidentsPage })));
const IncidentDetailPage = lazy(() => import('@features/incidents/IncidentDetailPage').then((m) => ({ default: m.IncidentDetailPage })));
const ProfilePage         = lazy(() => import('@features/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const DictionariesPage    = lazy(() => import('@features/admin/DictionariesPage').then((m) => ({ default: m.DictionariesPage })));
const SystemSettingsPage  = lazy(() => import('@features/admin/SystemSettingsPage').then((m) => ({ default: m.SystemSettingsPage })));
const PlannerPage         = lazy(() => import('@features/planner/PlannerPage').then((m) => ({ default: m.PlannerPage })));
const TaskTemplatesPage   = lazy(() => import('@features/task-templates/TaskTemplatesPage').then((m) => ({ default: m.TaskTemplatesPage })));
const FeedbackPage        = lazy(() => import('@features/feedback/FeedbackPage').then((m) => ({ default: m.FeedbackPage })));
const FeedbackMinePage    = lazy(() => import('@features/feedback/FeedbackMinePage').then((m) => ({ default: m.FeedbackMinePage })));
const TablicaPage         = lazy(() => import('@features/board/TablicaPage').then((m) => ({ default: m.TablicaPage })));
const HelpPage            = lazy(() => import('@features/help/HelpPage').then((m) => ({ default: m.HelpPage })));
const AuditPage           = lazy(() => import('@features/audit/AuditPage').then((m) => ({ default: m.AuditPage })));
const LegalLogPage        = lazy(() => import('@features/legal/LegalLogPage').then((m) => ({ default: m.LegalLogPage })));
const LegalCenterPage     = lazy(() => import('@features/legal/LegalCenterPage').then((m) => ({ default: m.LegalCenterPage })));
const DutyLogPage         = lazy(() => import('@features/duty-log/DutyLogPage').then((m) => ({ default: m.DutyLogPage })));
const FailuresPage        = lazy(() => import('@features/failures/FailuresPage').then((m) => ({ default: m.FailuresPage })));
// Moduł Ubezpieczenia i szkody (Admin 1.687.0, koncepcja zatwierdzona 18.10.2026)
const InsuranceDashboardPage = lazy(() => import('@features/insurance/dashboard/InsuranceDashboardPage').then((m) => ({ default: m.InsuranceDashboardPage })));
const InsurancePoliciesPage  = lazy(() => import('@features/insurance/policies/PoliciesPage').then((m) => ({ default: m.PoliciesPage })));
const InsuranceImportPage    = lazy(() => import('@features/insurance/policies/ImportPage').then((m) => ({ default: m.ImportPage })));
const InsuranceSettingsPage  = lazy(() => import('@features/insurance/settings/InsuranceSettingsPage').then((m) => ({ default: m.InsuranceSettingsPage })));
const ClaimsQueuePage        = lazy(() => import('@features/insurance/claims/QueuePage').then((m) => ({ default: m.QueuePage })));
const ClaimsPage             = lazy(() => import('@features/insurance/claims/ClaimsPage').then((m) => ({ default: m.ClaimsPage })));
const ClaimDetailPage        = lazy(() => import('@features/insurance/claims/ClaimDetailPage').then((m) => ({ default: m.ClaimDetailPage })));
const InsuranceAnalyticsPage = lazy(() => import('@features/insurance/analytics/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const TendersPage            = lazy(() => import('@features/insurance/tenders/TendersPage').then((m) => ({ default: m.TendersPage })));
const TenderDetailPage       = lazy(() => import('@features/insurance/tenders/TenderDetailPage').then((m) => ({ default: m.TenderDetailPage })));
const DisruptionsPage     = lazy(() => import('@features/disruptions/DisruptionsPage').then((m) => ({ default: m.DisruptionsPage })));
const MapaPojazdowPage    = lazy(() => import('@features/fleet-map/MapaPojazdowPage').then((m) => ({ default: m.MapaPojazdowPage })));
const InspectionsPage     = lazy(() => import('@features/inspections/InspectionsPage').then((m) => ({ default: m.InspectionsPage })));
const WorkTimePage        = lazy(() => import('@features/worktime/WorkTimePage').then((m) => ({ default: m.WorkTimePage })));
const ExecutionPage       = lazy(() => import('@features/execution/ExecutionPage').then((m) => ({ default: m.ExecutionPage })));
const RealizacjaWozowPage = lazy(() => import('@features/fleet-execution/RealizacjaWozowPage').then((m) => ({ default: m.RealizacjaWozowPage })));
const RoadCardsPage       = lazy(() => import('@features/road-cards/RoadCardsPage').then((m) => ({ default: m.RoadCardsPage })));
const TimetablePage       = lazy(() => import('@features/timetable/TimetablePage').then((m) => ({ default: m.TimetablePage })));
const NumberingPage       = lazy(() => import('@features/numbering/NumberingPage').then((m) => ({ default: m.NumberingPage })));
const FormNewPage         = lazy(() => import('@features/forms/FormNewPage').then((m) => ({ default: m.FormNewPage })));
const AdminLinesPage      = lazy(() => import('@features/admin/AdminLinesPage').then((m) => ({ default: m.AdminLinesPage })));
const DocumentsPage       = lazy(() => import('@features/documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage })));
const AdminDevicesPage    = lazy(() => import('@features/admin/AdminDevicesPage').then((m) => ({ default: m.AdminDevicesPage })));
const SocialPointsPage    = lazy(() => import('@features/admin/SocialPointsPage').then((m) => ({ default: m.SocialPointsPage })));
const RolesMatrixPage     = lazy(() => import('@features/settings/RolesMatrixPage').then((m) => ({ default: m.RolesMatrixPage })));
const GdprConsolePage       = lazy(() => import('@features/gdpr/GdprConsolePage').then((m) => ({ default: m.GdprConsolePage })));
const DataRequestPage       = lazy(() => import('@features/gdpr/DataRequestPage').then((m) => ({ default: m.DataRequestPage })));
const DataRequestVerifyPage = lazy(() => import('@features/gdpr/DataRequestPage').then((m) => ({ default: m.DataRequestVerifyPage })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-48">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <p className="text-6xl font-bold text-muted-foreground/30">404</p>
      <h2 className="text-xl font-bold mt-4">Page Not Found</h2>
      <a href="/" className="mt-4 text-primary hover:underline">← Back to Dashboard</a>
    </div>
  );
}

function Forbidden() {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <p className="text-6xl font-bold text-muted-foreground/30">403</p>
      <h2 className="text-xl font-bold mt-4">Access Denied</h2>
      <p className="text-muted-foreground mt-2">You don't have permission to view this page.</p>
      <a href="/" className="mt-4 text-primary hover:underline">← Back to Dashboard</a>
    </div>
  );
}

export default function App() {
  return (
    <>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route element={<PublicOnlyRoute />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Account self-service — reachable even with a stale session cookie */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/activate" element={<ActivatePage />} />

        {/* Public RODO data-subject request (no account required) */}
        <Route path="/data-request" element={<DataRequestPage />} />
        <Route path="/data-request/verify" element={<DataRequestVerifyPage />} />

        {/* Protected app routes */}
        <Route element={<ProtectedRoute />}>

          {/* Zmiana hasła — dostępna ZAWSZE gdy zalogowany (bez AppLayout, bez blokady) */}
          <Route path="change-password" element={<ChangePasswordPage />} />

          {/* Wszystkie pozostałe trasy — zablokowane gdy mustChangePassword=true */}
          <Route element={<MustChangePasswordGuard />}>
          <Route element={<AppLayout />}>
            {/* Dashboard */}
            <Route index element={<DashboardPage />} />

            {/* Forms */}
            <Route path="forms">
              <Route path="my" element={<FormsListPage />} />
              {/* SEC/§4: zakładanie tylko rolom zapisu — analityk wchodzący z adresu
                  dostałby pusty formularz, którego serwer i tak nie przyjmie. */}
              <Route element={<ProtectedRoute allowedRoles={[...ROLE_ZAPISU_FORMULARZY]} />}>
                <Route path="new/:code" element={<FormNewPage />} />
              </Route>
              {/* B-675: rejestr „Kontrole prędkości” — klucz `speed.registry.view` (menu, trasa i GET /speed-checks). */}
              <Route element={<ProtectedRoute permission="speed.registry.view" />}>
                <Route path="kontrole-predkosci" element={<KontrolePredkosciPage />} />
              </Route>
              {/* B-655: rejestr „Kontrole trzeźwości” — PRZED `:id` (czytelność; React Router i tak woli ścieżkę stałą).
                  Klucz `sobriety.registry.view` — tym samym pilnowane są menu i link z karty alkomatu. */}
              <Route element={<ProtectedRoute permission="sobriety.registry.view" />}>
                <Route path="kontrole-trzezwosci" element={<KontroleTrzezwosciPage />} />
              </Route>
              <Route path=":id" element={<FormPage />} />
            </Route>

            {/* Incidents */}
            <Route path="incidents">
              <Route index element={<IncidentsPage />} />
              <Route path=":id" element={<IncidentDetailPage />} />
            </Route>

            {/* Analytics */}
            <Route element={<ProtectedRoute permission="analytics.view" />}>
              <Route path="analytics" element={<AnalyticsPage />} />
            </Route>

            {/* Reports */}
            <Route element={<ProtectedRoute permission="reports.view" />}>
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/creator/:templateId" element={<ReportCreatorPage />} />
            </Route>

            {/* Instructors */}
            <Route element={<ProtectedRoute permission="drivers.manage" />}>
              <Route path="instructors" element={<InstructorsPage />} />
            </Route>

            {/* Planner */}
            <Route element={<ProtectedRoute permission="planner.manage" />}>
              <Route path="planner" element={<PlannerPage />} />
              <Route path="task-templates" element={<TaskTemplatesPage />} />
            </Route>

            {/* Dziennik służby */}
            <Route element={<ProtectedRoute permission="dutylog.view" />}>
              <Route path="duty-log" element={<DutyLogPage />} />
            </Route>

            {/* Ubezpieczenia i szkody — każda podstrona za swoim kluczem (permission-catalog.ts) */}
            <Route element={<ProtectedRoute permission="insurance.view" />}>
              <Route path="insurance" element={<InsuranceDashboardPage />} />
              <Route path="insurance/policies" element={<InsurancePoliciesPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="insurance.manage" />}>
              <Route path="insurance/import" element={<InsuranceImportPage />} />
              <Route path="insurance/settings" element={<InsuranceSettingsPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="claims.view" />}>
              <Route path="insurance/queue" element={<ClaimsQueuePage />} />
              <Route path="insurance/claims" element={<ClaimsPage />} />
              <Route path="insurance/claims/:id" element={<ClaimDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="insurance.analytics" />}>
              <Route path="insurance/analytics" element={<InsuranceAnalyticsPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="tenders.manage" />}>
              <Route path="insurance/tenders" element={<TendersPage />} />
              <Route path="insurance/tenders/:id" element={<TenderDetailPage />} />
            </Route>

            {/* Awarie */}
            <Route element={<ProtectedRoute permission="failures.view" />}>
              <Route path="failures" element={<FailuresPage />} />
            </Route>
            {/* B-305 (ZUR-2026-0079): utrudnienia w ruchu — osobne uprawnienie,
                bo krąg oglądających nie musi pokrywać się z awariami. */}
            <Route element={<ProtectedRoute permission="disruptions.view" />}>
              <Route path="disruptions" element={<DisruptionsPage />} />
            </Route>
            {/* B-519: mapa pojazdów — własne uprawnienie (kierownik zmiany + instruktor),
                do 05.10 wisiała tu pod `disruptions.view`. */}
            <Route element={<ProtectedRoute permission="fleet.map.view" />}>
              <Route path="mapa-pojazdow" element={<MapaPojazdowPage />} />
            </Route>

            {/* Przeglądy / Inspekcje */}
            <Route element={<ProtectedRoute permission="inspections.view" />}>
              <Route path="inspections" element={<InspectionsPage />} />
            </Route>

            {/* Rozliczenie czasu pracy */}
            <Route element={<ProtectedRoute permission="worktime.view" />}>
              <Route path="worktime" element={<WorkTimePage />} />
              {/* Wykonanie instruktorów — realizacja czynności zestawiona z planem */}
              <Route path="execution" element={<ExecutionPage />} />
            </Route>

            {/* B-616: Realizacja planu wozów — plan z rozkładu GAiT kontra DPK, KPI zmian, linie i brygady */}
            <Route element={<ProtectedRoute permission="reports.view" />}>
              <Route path="realizacja-wozow" element={<RealizacjaWozowPage />} />
            </Route>

            {/* B-676: Karty drogowe — przegląd wystawionych kart (rejestr, kafelki, kalendarz, pojazdy, instruktorzy, kolejka) */}
            <Route element={<ProtectedRoute permission="roadcards.view" />}>
              <Route path="road-cards" element={<RoadCardsPage />} />
            </Route>

            {/* B-683 E3: Rozkład jazdy z GAiT — paczki dni (lista, oś czasu, szczegóły służby z kursami i przystankami) */}
            <Route element={<ProtectedRoute permission="timetable.view" />}>
              <Route path="rozklad" element={<TimetablePage />} />
            </Route>

            {/* Feedback */}
            <Route path="feedback">
              <Route index element={<FeedbackPage />} />
              <Route path="mine" element={<FeedbackMinePage />} />
            </Route>

            {/* Tablica — komunikaty kierowników zmiany (B-61) */}
            <Route path="board" element={<TablicaPage />} />

            {/* Help / Centrum Pomocy */}
            <Route path="pomoc" element={<HelpPage />} />

            {/* Centrum Prawne — dokumenty do przeglądania (wszyscy zalogowani) */}
            <Route path="legal" element={<LegalCenterPage />} />

            {/* Notifications */}
            <Route path="notifications" element={<NotificationsPage />} />
            {/* Messages */}
            <Route path="messages" element={<MessagesPage />} />
            <Route path="profile" element={<ProfilePage />} />

            {/* Settings — Role i uprawnienia (RBAC matrix). Reguły czasu pracy
                przeniesione do Ustawień systemowych → sekcja „Czas pracy (planer)";
                stary adres przekierowuje tam dla zachowania zakładek/linków. */}
            <Route path="settings">
              <Route element={<ProtectedRoute permission="roles.manage" />}>
                <Route path="roles" element={<RolesMatrixPage />} />
              </Route>
              <Route path="working-time" element={<Navigate to="/admin/system?sekcja=workingtime" replace />} />
            </Route>

            {/* RODO — data subject rights console (B-17) */}
            <Route element={<ProtectedRoute permission="gdpr.export" />}>
              <Route path="admin/gdpr" element={<GdprConsolePage />} />
            </Route>

            {/* DPK — konsola synchronizacji (B-13) mieszka w Ustawieniach systemowych
                (sekcja „Synchronizacja DPK”, 2026-09-28); stary adres przekierowuje. */}
            <Route path="admin/external-dispatch" element={<Navigate to="/admin/system?sekcja=dpk" replace />} />

            {/* Admin — per-route RBAC permissions (matrix on /settings/roles) */}
            <Route path="admin">
              <Route index element={<Navigate to="/admin/users" replace />} />
              <Route element={<ProtectedRoute permission="users.manage" />}>
                <Route path="users" element={<AdminUsersPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="drivers.manage" />}>
                <Route path="drivers" element={<AdminDriversPage />} />
              </Route>
              {/* B-705: własna strona — do weryfikacji wystarcza `drivers.cepik.view` (wcześniej trzeba było też `drivers.manage`). */}
              <Route element={<ProtectedRoute permission="drivers.cepik.view" />}>
                <Route path="cepik" element={<CepikVerificationPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="vehicles.manage" />}>
                <Route path="vehicles" element={<AdminVehiclesPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="planner.manage" />}>
                <Route path="tasks" element={<AdminTaskAssignmentsPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="forms.manage" />}>
                <Route path="forms">
                  <Route index element={<FormBuilderListPage />} />
                  <Route path="new" element={<FormDesignerPage />} />
                  <Route path=":id" element={<FormDesignerPage />} />
                </Route>
              </Route>
              <Route element={<ProtectedRoute permission="dictionaries.manage" />}>
                <Route path="dictionaries" element={<DictionariesPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="lines.manage" />}>
                <Route path="lines" element={<AdminLinesPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="vehicles.manage" />}>
                <Route path="devices" element={<AdminDevicesPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="socialpoints.manage" />}>
                <Route path="social-points" element={<SocialPointsPage />} />
                {/* „Stan piasku” to od 26.10 zakładka Punktów socjalnych — stary adres zostaje dla zakładek przeglądarki. */}
                <Route path="social-points/sand" element={<Navigate to="/admin/social-points?widok=piasek" replace />} />
              </Route>
              <Route element={<ProtectedRoute permission="forms.manage" />}>
                <Route path="documents" element={<DocumentsPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="settings.manage" />}>
                <Route path="system" element={<SystemSettingsPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="audit.view" />}>
                <Route path="audit" element={<AuditPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="numbering.manage" />}>
                <Route path="numbering" element={<NumberingPage />} />
              </Route>
              <Route element={<ProtectedRoute permission="legal.view" />}>
                <Route path="legal" element={<LegalLogPage />} />
              </Route>
            </Route>

            {/* 403 */}
            <Route path="403" element={<Forbidden />} />
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Route>
          </Route> {/* MustChangePasswordGuard */}
        </Route>

        {/* Fallback redirect */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
    {/* B-556: poza Suspense, żeby ruszył od razu, a nie dopiero po załadowaniu pierwszego leniwego ekranu.
        Sprawdza, czy stoi nowsza wersja panelu, i wymienia kartę w bezpiecznym momencie. */}
    <AppUpdateWatcher />
    </>
  );
}
